import { Button, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { Document, Page, pdfjs } from 'react-pdf';
import { useEffect, useMemo, useState } from 'react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker for Vite
// Use CDN for the worker to avoid bundling issues
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export type FileViewerModalProps = {
    isOpen: boolean;
    toggle: () => void;
    fileUrl: string; // current file url (kept for backward compatibility)
    filename: string; // current filename (kept for backward compatibility)
    // Optional: list of files to allow cycling through multiple uploads
    files?: Array<{ url: string; filename: string }>;
    currentIndex?: number; // starting index into files
};

export const FileViewerModal = (props: FileViewerModalProps) => {
    const { isOpen, toggle } = props;
    const deriveFilenameFromUrl = (url?: string): string => {
        if (!url) return '';
        try {
            const u = new URL(url);
            const last = u.pathname.split('/').pop() || '';
            return decodeURIComponent(last);
        } catch {
            const path = (url || '').split('?')[0];
            return decodeURIComponent(path.split('/').pop() || '');
        }
    };
    const files = useMemo<Array<{ url: string; filename: string }>>(() => {
        const base = (props.files && props.files.length > 0)
            ? props.files
            : [{ url: props.fileUrl, filename: props.filename }];
        return base.map(f => ({
            url: f.url,
            filename: f.filename || deriveFilenameFromUrl(f.url),
        }));
    }, [props.files, props.fileUrl, props.filename]);

    const [fileIndex, setFileIndex] = useState<number>(0);

    useEffect(() => {
        const next = Math.min(Math.max(props.currentIndex ?? 0, 0), Math.max(0, files.length - 1));
        setFileIndex(next);
        // reset PDF page state when the list or index resets
        setNumPages(0);
        setPageNumber(1);
        setPdfError('');
    }, [files, props.currentIndex]);

    const current = files[fileIndex] ?? { url: props.fileUrl, filename: props.filename };
    const [numPages, setNumPages] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pdfError, setPdfError] = useState<string>('');

    // Determine file type based on filename extension
    const getFileExtension = (name?: string, url?: string) => {
        let candidate = name && name.includes('.') ? name : undefined;
        if (!candidate && url) {
            try {
                const u = new URL(url);
                candidate = u.pathname;
            } catch {
                candidate = url.split('?')[0];
            }
        }
        const last = (candidate || '').split('/').pop() || '';
        return last.split('.').pop()?.toLowerCase();
    };

    const fileExtension = getFileExtension(current.filename, current.url);
    const isPDF = fileExtension === 'pdf';
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension || '');

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
        setPageNumber(1);
        setPdfError('');
    };

    const onDocumentLoadError = (error: Error) => {
        console.error('PDF load error:', error);
        setPdfError('Failed to load PDF. Click to download instead.');
    };

    const changePage = (offset: number) => {
        setPageNumber(prevPageNumber => Math.min(Math.max(prevPageNumber + offset, 1), numPages));
    };

    const goToPage = (page: number) => {
        setPageNumber(Math.min(Math.max(page, 1), numPages));
    };

    const changeFile = (offset: number) => {
        const nextIndex = Math.min(
            Math.max(fileIndex + offset, 0),
            files.length - 1
        );
        if (nextIndex !== fileIndex) {
            setFileIndex(nextIndex);
            // reset PDF page state for new file
            setNumPages(0);
            setPageNumber(1);
            setPdfError('');
        }
    };

    return (
        <Modal centered isOpen={isOpen} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle}>
                {current.filename}
                {isPDF && numPages > 0 && (
                    <small className="ms-2 text-muted">
                        Page {pageNumber} of {numPages}
                    </small>
                )}
            </ModalHeader>
            <ModalBody>
                <div style={{ textAlign: 'center' }}>
                    {isPDF ? (
                        <div>
                            {pdfError ? (
                                <div>
                                    <p className="text-danger">{pdfError}</p>
                                    <Button 
                                        color="primary" 
                                        onClick={() => window.open(current.url, '_blank')}
                                    >
                                        Download PDF
                                    </Button>
                                </div>
                            ) : (
                                <>
                                    <div style={{ height: '70vh', overflow: 'auto', border: '1px solid #ddd' }}>
                                        <Document
                                            file={current.url}
                                            onLoadSuccess={onDocumentLoadSuccess}
                                            onLoadError={onDocumentLoadError}
                                            loading={<div>Loading PDF...</div>}
                                        >
                                            <Page 
                                                pageNumber={pageNumber}
                                                scale={1.2}
                                                loading={<div>Loading page...</div>}
                                            />
                                        </Document>
                                    </div>
                                    {numPages > 1 && (
                                        <div className="d-flex justify-content-center align-items-center mt-3" style={{ gap: '12px' }}>
                                            <button
                                                type="button"
                                                onClick={() => changePage(-1)}
                                                disabled={pageNumber <= 1}
                                                style={{
                                                    width: '36px',
                                                    height: '36px',
                                                    borderRadius: '50%',
                                                    border: '2px solid #000',
                                                    backgroundColor: 'white',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: pageNumber <= 1 ? 'not-allowed' : 'pointer',
                                                    opacity: pageNumber <= 1 ? 0.4 : 1,
                                                    fontSize: '16px',
                                                    fontWeight: 'bold',
                                                    transition: 'all 0.2s'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (pageNumber > 1) {
                                                        e.currentTarget.style.backgroundColor = '#f0f0f0';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = 'white';
                                                }}
                                            >
                                                ‹
                                            </button>
                                            <span style={{ fontSize: '14px', fontWeight: '500', minWidth: '80px', textAlign: 'center' }}>
                                                Page {pageNumber} of {numPages}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => changePage(1)}
                                                disabled={pageNumber >= numPages}
                                                style={{
                                                    width: '36px',
                                                    height: '36px',
                                                    borderRadius: '50%',
                                                    border: '2px solid #000',
                                                    backgroundColor: 'white',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: pageNumber >= numPages ? 'not-allowed' : 'pointer',
                                                    opacity: pageNumber >= numPages ? 0.4 : 1,
                                                    fontSize: '16px',
                                                    fontWeight: 'bold',
                                                    transition: 'all 0.2s'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (pageNumber < numPages) {
                                                        e.currentTarget.style.backgroundColor = '#f0f0f0';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = 'white';
                                                }}
                                            >
                                                ›
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    ) : isImage ? (
                        <img 
                            src={current.url} 
                            alt={current.filename}
                            style={{
                                maxWidth: '100%',
                                maxHeight: '70vh',
                                objectFit: 'contain'
                            }}
                        />
                    ) : (
                        <div>
                            <p>File type not supported for preview</p>
                            <Button 
                                color="primary" 
                                onClick={() => window.open(current.url, '_blank')}
                            >
                                Download File
                            </Button>
                        </div>
                    )}
                </div>
                
                {/* List of documents - only show if there are multiple files */}
                {files.length > 1 && (
                    <div className="mt-3" style={{ 
                        display: 'flex',
                        gap: '8px',
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        paddingBottom: '8px',
                        flexWrap: 'wrap'
                    }}>
                        {files.map((file, idx) => (
                            <button
                                key={idx}
                                type="button"
                                onClick={() => {
                                    setFileIndex(idx);
                                    setNumPages(0);
                                    setPageNumber(1);
                                    setPdfError('');
                                }}
                                style={{
                                    padding: '10px 20px',
                                    cursor: 'pointer',
                                    borderRadius: '25px',
                                    backgroundColor: idx === fileIndex ? '#4db8c4' : '#d1ecf1',
                                    color: idx === fileIndex ? '#fff' : '#0c5460',
                                    fontSize: '14px',
                                    border: 'none',
                                    fontWeight: idx === fileIndex ? '600' : '400',
                                    transition: 'all 0.2s',
                                    whiteSpace: 'nowrap',
                                    boxShadow: idx === fileIndex ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                                }}
                                onMouseEnter={(e) => {
                                    if (idx !== fileIndex) {
                                        e.currentTarget.style.backgroundColor = '#bee5eb';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (idx !== fileIndex) {
                                        e.currentTarget.style.backgroundColor = '#d1ecf1';
                                    }
                                }}
                            >
                                {file.filename}
                            </button>
                        ))}
                    </div>
                )}
                
                <div className='d-flex justify-content-between align-items-center mt-3'>
                    <div className="d-flex gap-2">
                        <Button 
                            onClick={toggle}
                            style={{
                                backgroundColor: 'white',
                                color: '#000',
                                border: '2px solid #000',
                                borderRadius: '25px',
                                padding: '10px 30px',
                                fontSize: '14px',
                                fontWeight: '600',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}
                        >
                            Close
                        </Button>
                        {(isPDF || isImage) && (
                            <Button 
                                onClick={() => window.open(current.url, '_blank')}
                                style={{
                                    backgroundColor: '#f4c542',
                                    color: '#000',
                                    border: 'none',
                                    borderRadius: '25px',
                                    padding: '10px 30px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}
                            >
                                Download
                            </Button>
                        )}
                    </div>
                    
                    {/* Circular navigation buttons in bottom right */}
                    {files.length > 1 && (
                        <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                            <button
                                type="button"
                                onClick={() => changeFile(-1)}
                                disabled={fileIndex <= 0}
                                style={{
                                    width: '44px',
                                    height: '44px',
                                    borderRadius: '50%',
                                    border: '2px solid #000',
                                    backgroundColor: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: fileIndex <= 0 ? 'not-allowed' : 'pointer',
                                    opacity: fileIndex <= 0 ? 0.4 : 1,
                                    fontSize: '20px',
                                    fontWeight: 'bold',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    if (fileIndex > 0) {
                                        e.currentTarget.style.backgroundColor = '#f0f0f0';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'white';
                                }}
                            >
                                ‹
                            </button>
                            <button
                                type="button"
                                onClick={() => changeFile(1)}
                                disabled={fileIndex >= files.length - 1}
                                style={{
                                    width: '44px',
                                    height: '44px',
                                    borderRadius: '50%',
                                    border: '2px solid #000',
                                    backgroundColor: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: fileIndex >= files.length - 1 ? 'not-allowed' : 'pointer',
                                    opacity: fileIndex >= files.length - 1 ? 0.4 : 1,
                                    fontSize: '20px',
                                    fontWeight: 'bold',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    if (fileIndex < files.length - 1) {
                                        e.currentTarget.style.backgroundColor = '#f0f0f0';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'white';
                                }}
                            >
                                ›
                            </button>
                        </div>
                    )}
                </div>
            </ModalBody>
        </Modal>
    );
};
